import { TableCellTypes, Radio } from '@carboledger/ui';
import {
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeDiscrepanciesCell,
  makeAssignQuantityCell,
  makeRemoveCell,
} from '../tableCells';
import { shipmentUnits } from './linkUnits';

// Columns for the Link shipments modal.

const renderUnitTypeCell = makeUnitTypeCell((t, row) =>
  shipmentUnits.buildRowUnitOptions(t, row.byUnit),
);
const renderAssignedQuantity = makeUnitScopedCell('assignedQuantity');
const renderUnassignedQuantity = makeUnitScopedCell('unassignedQuantity');
const renderShipmentQuantity = makeUnitScopedCell('shipmentQuantity');

// Sustainability + dispatch columns, identical in every step and both flows.
const sustainabilityColumns = (t, onOpenDiscrepancy) => [
  {
    id: 'ghgEmissions',
    header: t('FIELD_GHG_EMISSIONS'),
    type: TableCellTypes.text,
    width: 180,
  },
  {
    id: 'ghgEmissionSavings',
    header: t('LINK_SHIPMENTS_COL_GHG_SAVINGS'),
    type: TableCellTypes.text,
    width: 140,
  },
  {
    id: 'rawMaterialType',
    header: t('FIELD_RAW_MATERIAL_TYPE'),
    type: TableCellTypes.text,
    width: 200,
  },
  {
    id: 'countryOfOrigin',
    header: t('FIELD_COUNTRY_OF_ORIGIN'),
    type: TableCellTypes.text,
    width: 160,
  },
  {
    id: 'feedstockCategory',
    header: t('FIELD_FEEDSTOCK_CATEGORY'),
    type: TableCellTypes.text,
    width: 160,
  },
  {
    id: 'allocationPreference',
    header: t('FIELD_ALLOCATION_PREFERENCE'),
    type: TableCellTypes.text,
    width: 180,
  },
  {
    id: 'dateOfDispatch',
    header: t('FIELD_DATE_OF_DISPATCH'),
    type: TableCellTypes.text,
    width: 150,
  },
  {
    id: 'discrepancies',
    header: t('FIELD_DISCREPANCIES'),
    type: TableCellTypes.custom,
    width: 180,
    cell: makeDiscrepanciesCell(onOpenDiscrepancy),
  },
];

const unitTypeColumn = (t, headerSelect) => ({
  id: 'unitType',
  header: t('FIELD_UNIT_TYPE', {
    unit: shipmentUnits.unitTypeLabel(t, headerSelect.value),
  }),
  type: TableCellTypes.custom,
  width: 252,
  cell: renderUnitTypeCell,
  headerSelect,
});

/**
 * Step 1 — the shipments to pick from. `selectMode` is 'multi' (the Table's own
 * checkbox column) or 'single', which needs an app-owned radio column instead.
 */
export const buildLinkStep1Columns = (
  t,
  { headerSelect, selectMode, selectedId, onSelect, onOpenDiscrepancy },
) => {
  const columns = [
    {
      id: 'shipmentId',
      header: t('FIELD_SHIPMENT_ID'),
      type: TableCellTypes.text,
      width: 140,
    },
    {
      id: 'totalContractsLinked',
      header: t('FIELD_TOTAL_CONTRACTS_LINKED'),
      type: TableCellTypes.text,
      width: 160,
    },
    unitTypeColumn(t, headerSelect),
    {
      id: 'assignedQuantity',
      header: t('FIELD_ASSIGNED_QUANTITY_CONTRACT'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderAssignedQuantity,
    },
    {
      id: 'unassignedQuantity',
      header: t('FIELD_UNASSIGNED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderUnassignedQuantity,
    },
    {
      id: 'shipmentQuantity',
      header: t('FIELD_SHIPMENT_QUANTITY'),
      type: TableCellTypes.custom,
      width: 180,
      cell: renderShipmentQuantity,
    },
    ...sustainabilityColumns(t, onOpenDiscrepancy),
  ];

  if (selectMode === 'multi') return columns;

  // One shipment serves every selected contract, so the pick is a radio the app owns.
  // TODO: name the control once the library Radio takes an aria-label.
  const RadioCell = ({ row }) => (
    <Radio
      name="linkShipment"
      value={String(row.id)}
      checked={selectedId === row.id}
      onChange={() => onSelect(row.id)}
    />
  );
  return [
    {
      id: '__pick__',
      header: '',
      type: TableCellTypes.custom,
      width: 56,
      resizable: false,
      reorderable: false,
      cell: (ctx) => <RadioCell row={ctx.row} />,
    },
    ...columns,
  ];
};

// Step 2 (single flow) — the chosen shipments, each with an assign-quantity input.
export const buildLinkStep2Columns = (
  t,
  { headerSelect, assignments, onAssign, onOpenDiscrepancy },
) => {
  const renderAssign = makeAssignQuantityCell(assignments, onAssign);

  return [
    {
      id: 'shipmentId',
      header: t('FIELD_SHIPMENT_ID'),
      type: TableCellTypes.text,
      width: 140,
    },
    {
      id: 'assignQuantity',
      header: t('LINK_ASSIGN_QUANTITY'),
      type: TableCellTypes.custom,
      width: 240,
      cell: renderAssign,
    },
    {
      id: 'totalContractsLinked',
      header: t('FIELD_TOTAL_CONTRACTS_LINKED'),
      type: TableCellTypes.text,
      width: 160,
    },
    unitTypeColumn(t, headerSelect),
    {
      id: 'unassignedQuantity',
      header: t('FIELD_UNASSIGNED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderUnassignedQuantity,
    },
    {
      id: 'assignedQuantity',
      header: t('FIELD_ASSIGNED_QUANTITY_CONTRACT'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderAssignedQuantity,
    },
    {
      id: 'shipmentQuantity',
      header: t('FIELD_SHIPMENT_QUANTITY'),
      type: TableCellTypes.custom,
      width: 180,
      cell: renderShipmentQuantity,
    },
    ...sustainabilityColumns(t, onOpenDiscrepancy),
  ];
};

/**
 * Step 2 (multi flow) — one row per selected CONTRACT. The rows are the contracts
 * table's own view models, so only the assign input and Remove are new.
 */
export const buildLinkContractColumns = (t, { headerSelect, assignments, onAssign, onRemove }) => {
  const renderAssign = makeAssignQuantityCell(assignments, onAssign);
  const renderRemove = makeRemoveCell(onRemove);

  return [
    {
      id: 'contractId',
      header: t('FIELD_CONTRACT_ID'),
      type: TableCellTypes.text,
      width: 140,
    },
    unitTypeColumn(t, headerSelect),
    {
      id: 'assignQuantity',
      header: t('LINK_ASSIGN_QUANTITY'),
      type: TableCellTypes.custom,
      width: 240,
      cell: renderAssign,
    },
    {
      id: 'totalShipmentLinked',
      header: t('LINK_SHIPMENTS_COL_TOTAL_SHIPMENTS_LINKED'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: 'contractedQuantity',
      header: t('FIELD_CONTRACTED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: makeUnitScopedCell('contractedQuantity'),
    },
    {
      id: 'shippedQuantity',
      header: t('FIELD_SHIPPED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: makeUnitScopedCell('shippedQuantity'),
    },
    {
      id: 'outstandingQuantity',
      header: t('FIELD_OUTSTANDING_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: makeUnitScopedCell('outstandingQuantity'),
    },
    {
      id: 'deliverBy',
      header: t('FIELD_DELIVER_BY'),
      type: TableCellTypes.date,
      width: 150,
    },
    {
      id: '__remove__',
      header: t('FIELD_ACTION'),
      type: TableCellTypes.custom,
      width: 120,
      resizable: false,
      cell: renderRemove,
    },
  ];
};
